import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { Row, Col, Card, Form } from 'react-bootstrap'
import {
  Typography,
  FullPageContainer,
  ScrollableContainer,
  CustomInputWrapper,
} from '../components/styled'
import NotificationBoxPage from '../components/notificationBoxPage'
import PrimaryButton from '../components/buttons/PrimaryButton'
import theme from '../constants/theme'
import apiCustomAssessment from '../services/api/customAssessment'
import storageApi from '../services/firebaseStorage'

import FullLogo from '../assets/logos/full-logo.png'
import LanguagesDropdown from '../components/languageDropdwn'

function formatTime(seconds) {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function RadioInput({ value, onChange }) {
  return (
    <div className="mt-2">
      {['Yes', 'No'].map((opt) => (
        <CustomInputWrapper
          key={opt}
          checked={value === opt}
          onClick={() => onChange(opt)}
        >
          <input
            type="radio"
            checked={value === opt}
            onChange={() => {}} // Handled by wrapper
          />
          <span style={{ fontSize: '14px' }}>{opt}</span>
        </CustomInputWrapper>
      ))}
    </div>
  )
}

RadioInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
}

function NumberInput({ value, onChange }) {
  return (
    <Form.Control
      type="number"
      className="mt-2"
      style={{ maxWidth: 200, fontSize: '13px' }}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

NumberInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
}

function RangeInput({ value, onChange }) {
  const { t } = useTranslation()
  const v = value || {}
  return (
    <div className="d-flex gap-3 align-items-center mt-2">
      <div>
        <Form.Label style={{ fontSize: '12px', color: '#6c757d' }}>
          {t('rangeQuestion.from')}
        </Form.Label>
        <Form.Control
          type="number"
          style={{ maxWidth: 120, fontSize: '13px' }}
          value={v.start ?? ''}
          onChange={(e) => onChange({ ...v, start: e.target.value })}
        />
      </div>
      <div>
        <Form.Label style={{ fontSize: '12px', color: '#6c757d' }}>
          {t('rangeQuestion.to')}
        </Form.Label>
        <Form.Control
          type="number"
          style={{ maxWidth: 120, fontSize: '13px' }}
          value={v.end ?? ''}
          onChange={(e) => onChange({ ...v, end: e.target.value })}
        />
      </div>
    </div>
  )
}

RangeInput.propTypes = {
  value: PropTypes.shape({
    start: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    end: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onChange: PropTypes.func.isRequired,
}

function ChooseInput({ question, value, onChange }) {
  const optionsObj = question.answer?.options || {}
  const options = Object.keys(optionsObj)
  const selected = value || []

  const toggle = (label) => {
    const next = selected.includes(label)
      ? selected.filter((l) => l !== label)
      : [...selected, label]
    onChange(next)
  }

  return (
    <div className="mt-2">
      {options.map((label) => (
        <CustomInputWrapper
          key={label}
          checked={selected.includes(label)}
          onClick={() => toggle(label)}
        >
          <input
            type="checkbox"
            checked={selected.includes(label)}
            onChange={() => {}} // Handled by wrapper
          />
          <span style={{ fontSize: '14px' }}>{label}</span>
        </CustomInputWrapper>
      ))}
    </div>
  )
}

ChooseInput.propTypes = {
  question: PropTypes.shape({
    answer: PropTypes.shape({
      options: PropTypes.objectOf(PropTypes.string),
    }),
  }).isRequired,
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
}

function TextInput({ value, onChange }) {
  return (
    <Form.Control
      as="textarea"
      rows={3}
      className="mt-2"
      style={{ fontSize: '13px', resize: 'none' }}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

TextInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
}

function AttachmentInput({ value, onChange, onUploading }) {
  const { t } = useTranslation()
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [fileName, setFileName] = useState('')
  const [uploadError, setUploadError] = useState('')

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    setFileName(file.name)
    setUploading(true)
    setUploadError('')
    setProgress(0)
    onUploading?.(true)

    try {
      const blobPath = await storageApi.uploadFile(
        file,
        setProgress,
        null,
        null,
      )
      onChange(blobPath)
    } catch {
      setUploadError(t('assessment.uploadError'))
      onChange(null)
    } finally {
      setUploading(false)
      onUploading?.(false)
    }
  }

  const isUploaded = value && typeof value === 'string'

  return (
    <div className="mt-2">
      <Form.Control
        type="file"
        style={{ fontSize: '13px' }}
        onChange={handleFileChange}
        disabled={uploading}
        accept=".pdf,.doc,.docx,.zip,.txt,.png,.jpg,.jpeg,.csv,.xlsx"
      />

      {uploading && (
        <div className="mt-2">
          <div className="d-flex align-items-center gap-2 mb-1">
            <div
              className="spinner-border spinner-border-sm text-primary"
              role="status"
            />
            <Typography size="12px" color="#666">
              {t('assessment.uploading')} {progress}%
            </Typography>
          </div>
          <div style={{ height: 4, background: '#e9ecef', borderRadius: 2 }}>
            <div
              style={{
                height: '100%',
                width: `${progress}%`,
                background: theme.talentinoBlue || '#0d6efd',
                borderRadius: 2,
                transition: 'width 0.2s ease',
              }}
            />
          </div>
        </div>
      )}

      {isUploaded && !uploading && (
        <Typography size="12px" className="mt-1" color="#198754">
          {t('assessment.uploadSuccess')}{' '}
          {fileName || t('assessment.fileUploaded')}
        </Typography>
      )}

      {uploadError && (
        <Typography
          size="12px"
          className="mt-1"
          color={theme.talentinoDarkerRed || '#dc3545'}
        >
          {uploadError}
        </Typography>
      )}
    </div>
  )
}

AttachmentInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onUploading: PropTypes.func,
}

function detectLang(code) {
  if (!code || code.trim().length === 0) return 'plain text'
  const trimmed = code.trim()

  // 1. SQL & HTML (High confidence signatures)
  if (
    /^\s*(SELECT|INSERT|UPDATE|DELETE|CREATE|DROP|ALTER|WITH)\b/i.test(trimmed)
  )
    return 'sql'
  if (/^\s*<(!DOCTYPE|html|div|span|p|header|footer|section)/i.test(trimmed))
    return 'html'

  // 2. C++: Look for includes and pointers/namespaces
  if (
    /#include\s*<.*>|using\s+namespace\s+\w+|std::\w+|\b(cout|cin)\b/.test(
      trimmed,
    )
  )
    return 'cpp'

  // 3. JAVA: Look for specific keywords and the mandatory "System.out" or "String[] args"
  if (
    /\b(public\s+static\s+void\s+main|System\.out\.print|String\[\]\s+args)\b/.test(
      trimmed,
    )
  )
    return 'java'

  // 4. PHP: Open tag is definitive
  if (/<\?php|namespace\s+.*\\/.test(trimmed)) return 'php'

  // 5. PYTHON: Colons at the end of lines with no curly braces
  if (/\b(def|class)\s+\w+.*:/.test(trimmed) || /:\s*$/m.test(trimmed)) {
    if (!trimmed.includes('{')) return 'python'
  }

  // 6. JAVASCRIPT: Modern syntax fingerprints
  if (/\b(const|let)\s+\w+|=>|console\.log\(|export\s+default/.test(trimmed))
    return 'javascript'

  // 7. C-STYLE FALLBACK (Java vs C++ vs JS)
  // If we see curly braces but haven't triggered the above:
  if (trimmed.includes('{')) {
    // Java often uses 'String' (capitalized) and 'new' with types
    if (/\b(String|ArrayList|HashMap|Integer)\b/.test(trimmed)) return 'java'
    // C++ often uses 'int main()' or 'std::'
    if (/\bint\s+main\s*\(/.test(trimmed)) return 'cpp'
    // Default to JS if it looks like a web script
    return 'javascript'
  }

  return 'plain text'
}

export function CodeInput({ value, onChange }) {
  const { t } = useTranslation()
  const taRef = useRef(null)
  const lineNumRef = useRef(null)

  const lines = (value ?? '').split('\n')
  const lang = value?.trim() ? detectLang(value) : 'plain text'

  useEffect(() => {
    if (lineNumRef.current && taRef.current) {
      lineNumRef.current.scrollTop = taRef.current.scrollTop
    }
  })

  function handleKeyDown(e) {
    if (e.key === 'Tab') {
      e.preventDefault()
      const s = e.target.selectionStart
      const end = e.target.selectionEnd
      const next = value.substring(0, s) + '  ' + value.substring(end)
      onChange(next)
      requestAnimationFrame(() => {
        e.target.selectionStart = e.target.selectionEnd = s + 2
      })
    }
  }

  return (
    <div style={styles.wrap}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.dots}>
          {[0, 1, 2].map((i) => (
            <div key={i} style={styles.dot} />
          ))}
        </div>
        <div style={styles.headerRight}>
          <span style={styles.langBadge}>{lang}</span>
          {/* <button style={styles.copyBtn} onClick={handleCopy}>
            {copied ? "Copied!" : "Copy"}
          </button> */}
        </div>
      </div>

      {/* Body */}
      <div style={styles.body}>
        <div ref={lineNumRef} style={styles.lineNums}>
          {lines.map((_, i) => (
            <span key={i} style={styles.lineNum}>
              {i + 1}
            </span>
          ))}
        </div>
        <textarea
          ref={taRef}
          rows={Math.max(8, lines.length)}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onScroll={() => {
            if (lineNumRef.current && taRef.current)
              lineNumRef.current.scrollTop = taRef.current.scrollTop
          }}
          style={styles.textarea}
          spellCheck={false}
          placeholder="Paste or type your code here…"
        />
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        <span style={styles.stat}>
          {lines.length} {lines.length === 1 ? 'line' : 'lines'}
        </span>
        <span style={styles.stat}>
          {(value ?? '').length} {t('assessment.chars')}
        </span>
      </div>
    </div>
  )
}

CodeInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
}

const styles = {
  wrap: {
    border: '1px solid #2a2a2a',
    borderRadius: 8,
    overflow: 'hidden',
    fontFamily: 'monospace',
    fontSize: 13,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    background: '#252525',
    borderBottom: '1px solid #333',
  },
  dots: { display: 'flex', gap: 6 },
  dot: { width: 10, height: 10, borderRadius: '50%', background: '#444' },
  headerRight: { display: 'flex', alignItems: 'center', gap: 8 },
  langBadge: {
    fontSize: 11,
    color: '#888',
    padding: '2px 8px',
    border: '1px solid #333',
    borderRadius: 4,
  },
  copyBtn: {
    fontSize: 11,
    color: '#aaa',
    background: 'none',
    border: '1px solid #333',
    borderRadius: 4,
    padding: '2px 8px',
    cursor: 'pointer',
  },
  body: { display: 'flex', background: '#1e1e1e' },
  lineNums: {
    padding: '12px 0',
    background: '#252525',
    borderRight: '1px solid #2a2a2a',
    minWidth: 40,
    textAlign: 'right',
    overflowY: 'hidden',
    userSelect: 'none',
  },
  lineNum: {
    display: 'block',
    padding: '0 10px 0 8px',
    lineHeight: '20px',
    fontSize: 12,
    color: '#555',
  },
  textarea: {
    flex: 1,
    border: 'none',
    outline: 'none',
    resize: 'none',
    padding: 12,
    lineHeight: '20px',
    fontFamily: 'monospace',
    fontSize: 13,
    background: 'transparent',
    color: '#d4d4d4',
    caretColor: '#569cd6',
    whiteSpace: 'pre',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '6px 12px',
    background: '#252525',
    borderTop: '1px solid #333',
  },
  stat: { fontSize: 11, color: '#555' },
}

function QuestionInput({ question, value, onChange, onUploading }) {
  switch (question.answerType) {
    case 'radio':
      return <RadioInput value={value} onChange={onChange} />
    case 'number':
      return <NumberInput value={value} onChange={onChange} />
    case 'range':
      return <RangeInput value={value} onChange={onChange} />
    case 'choose':
      return (
        <ChooseInput question={question} value={value} onChange={onChange} />
      )
    case 'text':
      return <TextInput value={value} onChange={onChange} />
    case 'attachment':
      return (
        <AttachmentInput
          value={value}
          onChange={onChange}
          onUploading={onUploading}
        />
      )
    case 'code':
      return <CodeInput value={value} onChange={onChange} />
    default:
      return <TextInput value={value} onChange={onChange} />
  }
}

QuestionInput.propTypes = {
  question: PropTypes.shape({
    answerType: PropTypes.string.isRequired,
    answer: PropTypes.shape({
      options: PropTypes.objectOf(PropTypes.string),
    }),
  }).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.shape({}),
  ]),
  onChange: PropTypes.func.isRequired,
  onUploading: PropTypes.func,
}

export default function CandidateAssessment() {
  const { key: apiKey } = useParams()
  const { t } = useTranslation()

  const [screen, setScreen] = useState('loading')
  const [assessment, setAssessment] = useState(null)
  const [startedAt, setStartedAt] = useState(null)
  const [answers, setAnswers] = useState(() => {
    const saved = localStorage.getItem(`answers_${apiKey}`)
    return saved ? JSON.parse(saved) : {}
  })
  const [timeLeft, setTimeLeft] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [validationError, setValidationError] = useState('')
  const [uploadingCount, setUploadingCount] = useState(0)
  const timerRef = useRef(null)
  const handleSubmitRef = useRef(null)

  const handleUploadingState = (isUploading) => {
    setUploadingCount((prev) =>
      isUploading ? prev + 1 : Math.max(0, prev - 1),
    )
  }

  useEffect(() => {
    apiCustomAssessment
      .getCandidateAssessment(apiKey)
      .then((res) => {
        setAssessment(res.assessment || res)
        if (res.status === 'expired') {
          setScreen('expired')
        } else if (res.status === 'submitted' || res.status === 'evaluated') {
          setScreen('submitted')
        } else if (res.status === 'started') {
          if (res.startedAt) setStartedAt(res.startedAt)
          setScreen('active')
        } else {
          setScreen('intro')
        }
      })
      .catch((err) => {
        setErrorMessage(
          err?.response?.data?.message || t('assessment.somethingWrong'),
        )
        setScreen('error')
      })
  }, [apiKey, t])

  // Persistence Logic for Answers
  useEffect(() => {
    localStorage.setItem(`answers_${apiKey}`, JSON.stringify(answers))
  }, [answers, apiKey])

  // Timer Logic — startedAt from backend is the source of truth
  useEffect(() => {
    if (screen !== 'active' || !assessment?.durationMinutes) return

    const storageKey = `timer_${apiKey}`

    let endTime
    if (startedAt) {
      // Always recalculate from backend's started_at so refresh can't reset the clock
      endTime =
        new Date(startedAt).getTime() + assessment.durationMinutes * 60 * 1000
      localStorage.setItem(storageKey, endTime.toString())
    } else {
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        endTime = parseInt(saved, 10)
      } else {
        endTime = Date.now() + assessment.durationMinutes * 60 * 1000
        localStorage.setItem(storageKey, endTime.toString())
      }
    }

    // If the timer already ran out (e.g. user comes back after duration elapsed), auto-submit
    if (Date.now() >= endTime) {
      localStorage.removeItem(storageKey)
      handleSubmitRef.current?.(true)
      return
    }

    setTimeLeft(Math.round((endTime - Date.now()) / 1000))

    timerRef.current = setInterval(() => {
      const remaining = Math.round((endTime - Date.now()) / 1000)
      if (remaining <= 0) {
        clearInterval(timerRef.current)
        setTimeLeft(0)
        localStorage.removeItem(storageKey)
        handleSubmitRef.current?.(true)
      } else {
        setTimeLeft(remaining)
      }
    }, 1000)

    return () => clearInterval(timerRef.current)
  }, [screen, assessment, startedAt, apiKey])

  const handleStart = () => {
    apiCustomAssessment.startAssessment(apiKey).then((res) => {
      if (res?.startedAt) setStartedAt(res.startedAt)
      setScreen('active')
    })
  }

  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }))
  }

  const handleSubmit = (force = false) => {
    if (isSubmitting) return

    const questions = assessment?.questions || []

    const hasUnanswered = questions.some((q) => {
      const val = answers[q.id]
      if (val === null || val === undefined || val === '') return true
      if (Array.isArray(val) && val.length === 0) return true
      return false
    })

    if (!force) {
      if (hasUnanswered) {
        setValidationError(t('assessment.allQuestionsRequired'))
        return
      }
    } else if (hasUnanswered) {
      // Timer expired with unanswered questions — don't submit, show time-expired screen
      clearInterval(timerRef.current)
      localStorage.removeItem(`timer_${apiKey}`)
      localStorage.removeItem(`answers_${apiKey}`)
      setScreen('timeExpired')
      return
    }

    setValidationError('')
    setIsSubmitting(true)
    clearInterval(timerRef.current)

    const formattedAnswers = questions.map((q) => ({
      question_id: q.id,
      answer: answers[q.id] ?? null,
    }))

    apiCustomAssessment
      .submitAssessment(apiKey, { answers: formattedAnswers })
      .then(() => {
        setIsSubmitting(false)
        localStorage.removeItem(`timer_${apiKey}`)
        localStorage.removeItem(`answers_${apiKey}`)
        setScreen(force ? 'timeExpired' : 'submitted')
      })
      .catch(() => {
        setIsSubmitting(false)
        setScreen(force ? 'timeExpired' : 'submitted')
      })
  }

  handleSubmitRef.current = handleSubmit

  const translateAssessmentType = (type) => {
    const map = {
      'Technical Skills': t('assessment.types.technicalSkills'),
      'Problem Solving': t('assessment.types.problemSolving'),
      Behavioral: t('assessment.types.behavioral'),
    }
    return map[type] || type
  }

  if (screen === 'loading') {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '100vh' }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t('general.loading')}</span>
        </div>
      </div>
    )
  }

  if (screen === 'error') {
    return <NotificationBoxPage messageType="error" message={errorMessage} />
  }

  if (screen === 'expired') {
    return (
      <NotificationBoxPage
        messageType="warning"
        message={t('assessment.assessmentExpiredMessage')}
      />
    )
  }

  if (screen === 'submitted') {
    return (
      <NotificationBoxPage
        messageType="success"
        message={t('assessment.assessmentSubmittedMessage')}
      />
    )
  }

  if (screen === 'timeExpired') {
    return (
      <NotificationBoxPage
        messageType="warning"
        message={t('assessment.timeExpiredMessage')}
      />
    )
  }

  if (screen === 'intro') {
    return (
      <FullPageContainer>
        <ScrollableContainer className="mt-3 fw-light">
          <Row className="my-2 align-items-end">
            <Col xs="auto">
              <img
                height="auto"
                width="150px"
                src={FullLogo}
                alt={t('offerResponse.logoAlt')}
              />
            </Col>
            <Col />
            <Col xs="auto">
              <LanguagesDropdown />
            </Col>
          </Row>
          <Row className="mt-4 pb-5 justify-content-center">
            <Col xs={12} md={8} lg={7}>
              <Card className="p-5 shadow">
                <Typography bold size="24px" className="mb-2">
                  {assessment?.name}
                </Typography>
                <span
                  className="badge bg-secondary mb-3"
                  style={{ width: 'fit-content' }}
                >
                  {translateAssessmentType(assessment?.assessmentType)}
                </span>
                {assessment?.description && (
                  <div
                    className="mb-4 text-muted"
                    style={{ fontSize: 14 }}
                    dangerouslySetInnerHTML={{ __html: assessment.description }}
                  />
                )}

                <Row className="mb-4 g-3">
                  <Col xs={6}>
                    <Card className="p-3 text-center border-0 bg-light">
                      <Typography bold size="20px">
                        {assessment?.durationMinutes} {t('assessment.min')}
                      </Typography>
                      <Typography size="13px" className="text-muted">
                        {t('assessment.duration')}
                      </Typography>
                    </Card>
                  </Col>
                  <Col xs={6}>
                    <Card className="p-3 text-center border-0 bg-light">
                      <Typography bold size="20px">
                        {assessment?.questions?.length || 0}
                      </Typography>
                      <Typography size="13px" className="text-muted">
                        {t('interview.questions')}
                      </Typography>
                    </Card>
                    {/* <div
                      className="p-3 text-center"
                      style={{
                        backgroundColor: '#F8F9FA',
                        borderRadius: '8px',
                        border: `1px solid ${theme.talentinoWhiteBlue}`,
                      }}
                    >
                      <Typography bold size="20px" color={theme.talentinoBlack}>
                        {assessment?.questions?.length || 0}
                      </Typography>
                      <Typography
                        size="12px"
                        color={theme.talentinoSecondaryGray}
                      >
                        {t('interview.questions')}
                      </Typography>
                    </div> */}
                  </Col>
                </Row>

                <Typography
                  size="12px"
                  color={theme.talentinoSecondaryGray}
                  className="mb-4 d-block"
                >
                  {t('assessment.introInstructions')}
                </Typography>

                <PrimaryButton width="200px" onClick={handleStart}>
                  {t('assessment.startAssessment')}
                </PrimaryButton>
              </Card>
            </Col>
          </Row>
        </ScrollableContainer>
      </FullPageContainer>
    )
  }

  // Active screen
  const questions = assessment?.questions || []

  return (
    <FullPageContainer>
      <header
        style={{
          background: '#fff',
          borderBottom: '2px solid #eee',
          padding: '1rem 2rem',
        }}
      >
        <div className="container d-flex justify-content-between align-items-center">
          <img src={FullLogo} width="120px" alt="logo" />
          <div className="text-center">
            <Typography bold size="16px">
              {assessment?.name}
            </Typography>
            <Typography className="ms-2" size="12px" color="#666">
              {assessment?.questions?.length} {t('interview.questions')}
            </Typography>
          </div>
          <div
            style={{
              background: timeLeft < 60 ? '#FFF0F0' : '#F0F7FF',
              padding: '8px 15px',
              borderRadius: '8px',
              border: `1px solid ${timeLeft < 60 ? '#FF0000' : '#CCE5FF'}`,
            }}
          >
            <span style={{ fontSize: '12px', display: 'block', color: '#666' }}>
              {t('assessment.timeLeft')}
            </span>
            <span
              style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: timeLeft < 60 ? 'red' : 'black',
              }}
            >
              {formatTime(timeLeft)}
            </span>
          </div>
        </div>
      </header>

      {/* Visible scrollbar for the active question area */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarWidth: 'thin',
          scrollbarColor: '#CBD5E0 #F7FAFC',
          background: theme.talentinoSkyBlue,
        }}
      >
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              {/* Progress indicator */}
              <div className="d-flex justify-content-between align-items-center mb-4">
                <Typography size="13px" color="#666">
                  {
                    Object.keys(answers).filter((k) => {
                      const v = answers[k]
                      return (
                        v !== null &&
                        v !== undefined &&
                        v !== '' &&
                        !(Array.isArray(v) && v.length === 0)
                      )
                    }).length
                  }{' '}
                  / {questions.length} {t('assessment.answered')}
                </Typography>
                <div
                  style={{
                    height: 6,
                    flex: 1,
                    background: '#e9ecef',
                    borderRadius: 3,
                    margin: '0 12px',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${
                        questions.length
                          ? (Object.keys(answers).filter((k) => {
                              const v = answers[k]
                              return (
                                v !== null &&
                                v !== undefined &&
                                v !== '' &&
                                !(Array.isArray(v) && v.length === 0)
                              )
                            }).length /
                              questions.length) *
                            100
                          : 0
                      }%`,
                      background: theme.talentinoBlue || '#0d6efd',
                      borderRadius: 3,
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>
              </div>

              {questions.map((q, idx) => (
                <Card
                  key={q.id}
                  className="mb-4 border-0 shadow-sm"
                  style={{ borderRadius: '12px' }}
                >
                  <Card.Body className="p-4">
                    <div className="mb-3 d-flex align-items-start gap-2">
                      <span
                        style={{
                          minWidth: 28,
                          height: 28,
                          borderRadius: '50%',
                          background: theme.brandColorOrange || '#f97316',
                          color: '#fff',
                          fontSize: '13px',
                          fontWeight: 'bold',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          marginTop: 2,
                        }}
                      >
                        {idx + 1}
                      </span>
                      <Typography bold size="15px" color={theme.talentinoBlack}>
                        {q.question}
                      </Typography>
                    </div>
                    <QuestionInput
                      question={q}
                      value={answers[q.id]}
                      onChange={(val) => handleAnswerChange(q.id, val)}
                      onUploading={handleUploadingState}
                    />
                  </Card.Body>
                </Card>
              ))}

              <div className="d-flex flex-column align-items-end mt-4 pb-4">
                {validationError && (
                  <p
                    className="mb-2"
                    style={{
                      fontSize: '12px',
                      color: theme.talentinoDarkerRed,
                    }}
                  >
                    {validationError}
                  </p>
                )}
                {uploadingCount > 0 && (
                  <p
                    className="mb-2"
                    style={{ fontSize: '12px', color: '#666' }}
                  >
                    {t('assessment.uploading')}...
                  </p>
                )}
                <PrimaryButton
                  width="240px"
                  height="50px"
                  style={{ borderRadius: '10px', fontSize: '16px' }}
                  onClick={() => handleSubmit()}
                  disabled={isSubmitting || uploadingCount > 0}
                >
                  {isSubmitting
                    ? t('general.loading')
                    : t('assessment.submitAssessment')}
                </PrimaryButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </FullPageContainer>
  )
}
